import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  addToCart, 
  registerCheckout, 
  selectCartState, 
  selectIsUserLoggedIn, 
  selectUserInfo,
  selectWithcookieData,
  fetchCheckouts,
  fetchProductDetail,
  selectProductDetailState
} from '../../../redux/WithcookieSlice';
import { showToast } from '../../common/Toast';
import { useTranslation } from '../../../redux/TranslationHelper';
import { MembershipSignupProps, ProductPrices } from './MembershipSignup.types';
import { adaptMembershipSignupProps } from './MembershipSignup.adapter';
import './MembershipSignup.css';

/**
 * 멤버십 가입 신청 컴포넌트
 */
const MembershipSignupComponent: React.FC<any> = (props) => {

  // 어댑터를 통해 props 변환
  const adaptedProps = adaptMembershipSignupProps(props);
  

  
  const { t } = useTranslation();
  
  const {
    id = '',
    style = {},
    className = "membership-signup-wrapper",
    componentProps = {},
    isPreview = false,
    ...rest
  } = adaptedProps;
  
  const { 
    title = t("멤버십 가입신청"),
    productId = 0,
    buttonText = t("가입 신청"),
    completedText = t("가입 완료"),
    description = t("멤버십에 가입하시면 다양한 혜택을 받으실 수 있습니다.")
  } = componentProps;
  
  // productId를 안전하게 숫자로 변환
  const numericProductId = typeof productId === 'string' ? parseInt(productId, 10) || 0 : productId || 0;

  // 타입 오류 방지를 위한 any 타입 단언
  const dispatch = useDispatch() as any;
  
  // React Hooks 규칙 준수: 항상 최상위 레벨에서 Hook 호출
  const userLoggedInState = useSelector(selectIsUserLoggedIn);
  const userInfoState = useSelector(selectUserInfo);
  const withcookieDataState = useSelector(selectWithcookieData);
  const cartState = useSelector(selectCartState);
  const { productData, loading: productLoading, error: productError } = useSelector(selectProductDetailState);

  // Hook 호출 결과에 조건 적용
  const isUserLoggedIn = userLoggedInState;
  const userInfo = userInfoState;
  const withcookieData = withcookieDataState;
  const { loading: cartLoading } = cartState;

  const [isJoined, setIsJoined] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [isCanceled, setIsCanceled] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [quantity] = useState<number>(1); // 수량은 항상 1로 고정

  // 상품 정보 API 호출
  useEffect(() => {
    // 상품 ID 가져오기 (설정에서 지정한 값 사용)
    if (!numericProductId || numericProductId <= 0) {
      console.error(t('유효하지 않은 상품 ID:'), numericProductId, t('원본값:'), productId);
      setError(t('유효한 상품 ID를 설정해주세요.'));
      return;
    }
    
    dispatch(fetchProductDetail(numericProductId));
  }, [dispatch, numericProductId, productId, t]);

  // 주문 내역 가져오기
  useEffect(() => {
    if (!isUserLoggedIn) {
      return;
    }
    
    if (!productData || !productData.product) {
      return;
    }
    
    const productId = productData.product.id;
    if (!productId) {
      return;
    }
    
    if (!dispatch) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      dispatch(fetchCheckouts({ page: 1, take: 50 }))
        .unwrap()
        .then((response: any) => {
          // 체크아웃 목록을 상태로 저장하지 않고 바로 처리
          const checkoutsList = response && response.checkouts ? response.checkouts : [];
          
          // 해당 상품이 포함된 체크아웃 찾기
          const relevantCheckouts = checkoutsList.filter((checkout: any) => 
            checkout && checkout.carts && Array.isArray(checkout.carts) &&
            checkout.carts.some((cart: any) => 
              cart && cart.product && cart.product.id === productId
            )
          );
          
          // 체크아웃이 없거나 모두 CANCEL 상태인 경우 - 가입 가능
          if (relevantCheckouts.length === 0) {
            setIsJoined(false);
            setIsPending(false);
            setIsCanceled(false);
            return;
          }
          
          // 주문 상태 별로 구분하여 처리
          const hasCompleted = relevantCheckouts.some((checkout: any) => 
            checkout.status === 'PREPARING' || 
            checkout.status === 'COMPLETED' || 
            checkout.status === 'FINISHED'
          );
          
          const hasPending = relevantCheckouts.some((checkout: any) => 
            checkout.status === 'REQUESTED'
          );
          
          const allCanceled = relevantCheckouts.every((checkout: any) => 
            checkout.status === 'CANCEL'
          );
          
          // 가입 완료 상태가 있으면 최우선 처리
          if (hasCompleted) {
            setIsJoined(true);
            setIsPending(false);
            setIsCanceled(false);
          } 
          // 가입 대기 상태가 있으면 다음 우선순위
          else if (hasPending) {
            setIsJoined(false);
            setIsPending(true);
            setIsCanceled(false);
          }
          // 모두 취소 상태면 취소 처리
          else if (allCanceled) {
            setIsJoined(false);
            setIsPending(false);
            setIsCanceled(true);
          }
          // 그 외의 경우 (알 수 없는 상태)
          else {
            setIsJoined(false);
            setIsPending(false);
            setIsCanceled(false);
          }
        })
        .catch((err: any) => {
          console.error(t('[멤버십 가입] 주문 내역 조회 실패:'), err);
          setError(t('주문 내역을 불러오는데 실패했습니다.'));
        })
        .finally(() => {
          setIsLoading(false);
        });
    } catch (e) {
      console.error(t("[멤버십 가입] 주문 내역 조회 중 오류:"), e);
      setError(t("주문 내역을 불러오는데 실패했습니다."));
      setIsLoading(false);
    }
  }, [dispatch, isUserLoggedIn, productData, t]);

  // 가격 계산 함수
  const getProductPrices = (): ProductPrices => {
    if (!productData || !productData.product || !productData.product.config) {
      return {
        originalPrice: 0,
        discountedPrice: 0,
        hasDiscount: false,
        discountRate: "0%"
      };
    }

    const config = productData.product.config;
    // default_price를 안전하게 숫자로 변환 (문자열 또는 undefined일 수 있음)
    const originalPrice = parseInt(config.default_price || 0, 10);
    
    // discounted_price를 안전하게 숫자로 변환
    let discountedPrice = parseInt(config.discounted_price || 0, 10);
    
    // discounted_price가 0이거나 없는 경우 default_price 사용
    if (discountedPrice <= 0) {
      discountedPrice = originalPrice;
    }
    
    const hasDiscount = discountedPrice < originalPrice && discountedPrice > 0;
    
    // 할인율 계산
    let discountRate = "0%";
    if (hasDiscount && originalPrice > 0) {
      const rateValue = Math.round(100 - (discountedPrice / originalPrice * 100));
      discountRate = `${rateValue}%`;
    }

    return {
      originalPrice,
      discountedPrice,
      hasDiscount,
      discountRate
    };
  };

  // 가입 신청 전 확인 대화상자
  const confirmSignupRequest = () => {
    if (window.confirm(t("멤버십 가입을 신청하시겠습니까?"))) {
      handleSignupRequestProcess();
    }
  };

  // 가입 신청 처리
  const handleSignupRequest = (e: React.MouseEvent) => {
    // 이벤트 전파 방지
    e.preventDefault();
    e.stopPropagation();
    
    if (!isUserLoggedIn) {
      showToast(t('로그인이 필요합니다.'), 'warning');
      return;
    }

    if (!productData || !productData.product || !productData.product.id) {
      showToast(t('가입할 멤버십 상품이 없습니다.'), 'warning');
      return;
    }
    
    // 확인 대화상자 표시
    confirmSignupRequest();
  };
  
  // 실제 가입 신청 프로세스 실행
  const handleSignupRequestProcess = async () => {
    if (!productData || !productData.product) return;
    
    const productId = productData.product.id;

    if (!userInfo || !userInfo.id) {
      console.error(t('[멤버십 가입] 사용자 정보 누락:'), { userInfo, isUserLoggedIn });
      showToast(t('사용자 정보를 가져올 수 없습니다.'), 'error');
      return;
    }

    // 서비스 정보 검증 및 기본값 설정
    let companyId = 0;
    let skinId = 0;
    
    if (withcookieData && withcookieData.company && withcookieData.company.id) {
      companyId = withcookieData.company.id;
    } else if (userInfo && userInfo.companyId) {
      // 사용자 정보에서 companyId 가져오기
      companyId = userInfo.companyId;
    } else {
      companyId = 19; // 기본 companyId
    }
    
    if (withcookieData && withcookieData.skin && withcookieData.skin.id) {
      skinId = withcookieData.skin.id;
    } else {
      skinId = 1; // 기본 skinId
    }

    try {
      setIsLoading(true);
      setError(null);

      // 상품 가격 정보 가져오기
      const { discountedPrice } = getProductPrices();

      // 1. 장바구니에 상품 추가
      const cartResponse = await dispatch(addToCart({ 
        product_id: productId, 
        count: quantity
      })).unwrap();

      // 2. 체크아웃 생성 (sending 결제 방식)
      const checkoutData = {
        // 필수 사용자 정보 
        user_id: userInfo.id,
        company_id: companyId,
        skin_id: skinId,
        
        // 수신자 정보
        receiver_name: userInfo.name || '',
        receiver_phone: userInfo.phone || '',
        receiver_email: userInfo.email || '',
        
        // 금액 정보
        total_price: discountedPrice,   // 할인된 가격
        buy_price: discountedPrice,     // 할인된 가격
        parcel_price: 0,               // 배송비 없음
        
        // sending 결제 방식 (자동 결제 완료)
        payment_method: "SEND",
        
        // 주소 정보 (필수지만 값은 사용자 주소 또는 빈 값으로 설정)
        address: userInfo.address || '',
        address_detail: '',
        postal_code: '',
        
        // 장바구니 정보 (방금 추가한 상품)
        carts: [{
          product_id: productId,
          company_id: companyId,
          user_id: userInfo.id,
          count: quantity
        }]
      };

      const checkoutResponse = await dispatch(registerCheckout(checkoutData)).unwrap();

      // 성공 처리 (가입 완료가 아닌 신청 완료로 메시지 변경)
      setIsPending(true);
      showToast(t('멤버십 가입 신청이 완료되었습니다.'), 'success');

      // 주문 내역 다시 불러오기
      dispatch(fetchCheckouts({ page: 1, take: 50 }));

    } catch (err: any) {
      console.error(t('멤버십 가입 처리 중 오류:'), err);
      const errorMessage = err?.message || t('멤버십 가입 처리 중 오류가 발생했습니다.');
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // 로딩 중일 때 표시
  const renderLoading = () => {
    // HTML 아닌 속성들 제외
    const { content, productData, pathVariables, data, ...divProps } = rest;
    
    return (
      <div className={className} style={style} {...divProps}>
        <div className="container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>{t("처리 중입니다...")}</p>
          </div>
        </div>
      </div>
    );
  };

  // 에러 발생 시 표시
  const renderError = () => {
    // HTML 아닌 속성들 제외
    const { content, productData, pathVariables, data, ...divProps } = rest;
    
    return (
      <div className={className} style={style} {...divProps}>
        <div className="container">
          <div className="error-message">
            <p>{error || productError}</p>
            <button 
              className="retry-button"
              onClick={() => window.location.reload()}
            >
              {t("다시 시도")}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // 로그인이 필요한 경우 표시
  const renderLoginRequired = () => {
    // HTML 아닌 속성들 제외
    const { content, productData, pathVariables, data, ...divProps } = rest;
    
    return (
      <div className={className} style={style} {...divProps}>
        <div className="container">
          <div className="login-required">
            <h2 className="membership-title">{title}</h2>
            <p>{t("멤버십 가입을 위해 로그인이 필요합니다.")}</p>
            <a href="/login" className="login-button">{t("로그인하기")}</a>
          </div>
        </div>
      </div>
    );
  };

  // 상품이 없는 경우 표시
  const renderNoProduct = () => {
    // HTML 아닌 속성들 제외
    const { content, productData: pd, pathVariables, data, ...divProps } = rest;
    
    // 샘플 가격 추가
    const samplePrice = "450,000";
    
    if (isPreview) {
      // 프리뷰 모드에서는 샘플 데이터 표시
      return (
        <div className={className} style={{
          ...style,
          border: '1px solid #ddd',
          padding: '20px',
          borderRadius: '8px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
          backgroundColor: '#f9f9f9',
          margin: '20px 0'
        }} {...divProps}>
          <div className="container">
            <h2 className="membership-title">{title}</h2>
            <p className="membership-description">{description}</p>
            
            <div className="membership-product" style={{ 
              backgroundColor: '#fff', 
              padding: '15px', 
              borderRadius: '4px', 
              marginBottom: '20px'
            }}>
              <h3 style={{ marginBottom: '10px' }}>{t("샘플 멤버십 상품")}</h3>
              <p className="product-price" style={{ color: '#e74c3c', fontWeight: 'bold', fontSize: '18px' }}>
                {samplePrice}{t("원")}
              </p>
            </div>
            
            <button 
              className="signup-button"
              disabled={true}
              style={{
                backgroundColor: '#3498db',
                color: 'white',
                border: 'none',
                padding: '10px 20px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '16px',
                width: '100%'
              }}
            >
              {buttonText} {t("(프리뷰)")}
            </button>
          </div>
        </div>
      );
    }
    
    return (
      <div className={className} style={style} {...divProps}>
        <div className="container">
          <div className="error-message">
            <h2 className="membership-title">{title}</h2>
            <p>{t("표시할 멤버십 상품이 없습니다.")}</p>
          </div>
        </div>
      </div>
    );
  };

  // 실제 컴포넌트 렌더링 (프리뷰/에디터 구분 없이 동일하게 처리)
  if (isLoading || cartLoading || productLoading) {
    return renderLoading();
  }

  if (error || productError) {
    return renderError();
  }

  if (!isUserLoggedIn) {
    return renderLoginRequired();
  }

  // 상품 데이터 구조 확인 방식 변경
  if (!productData || !productData.product) {
    return renderNoProduct();
  }

  // 가격 정보 계산
  const { originalPrice, discountedPrice, hasDiscount, discountRate } = getProductPrices();

  // HTML 아닌 속성들 제외
  const { content, productData: pd, pathVariables, data, ...divProps } = rest;

  return (
    <div className={className} style={style} {...divProps}>
      <div className="container">
        <h2 className="membership-title">{title}</h2>
        <p className="membership-description">{description}</p>
        
        <div className="membership-product">
          <h3>{productData.product.title}</h3>
          <p className="product-description">{productData.product.description && 
            productData.product.description.substring(0, 100) + (productData.product.description.length > 100 ? '...' : '')}</p>
          
          {/* 할인가와 정상가 모두 표시 */}
          <div className="product-price-container">
            {hasDiscount && (
              <span className="product-original-price">
                {originalPrice.toLocaleString()}{t("원")}
              </span>
            )}
            <p className="product-price">
              {discountedPrice.toLocaleString()}{t("원")}
            </p>
            {hasDiscount && discountRate !== "0%" && (
              <span className="product-discount">{discountRate} {t("할인")}</span>
            )}
          </div>
        </div>
        
        {isJoined ? (
          <div className="signup-completed">
            <p>{completedText}</p>
          </div>
        ) : isPending ? (
          <div className="signup-pending">
            <p>{t("가입 승인 대기중")}</p>
          </div>
        ) : (
          <button 
            className="signup-button"
            onClick={isPreview ? undefined : handleSignupRequest}
            disabled={isLoading || isPreview}
          >
            {buttonText}{isPreview ? ` (${t("프리뷰")})` : ''}
          </button>
        )}
      </div>
    </div>
  );
};

export default MembershipSignupComponent; 